import { useQuery } from '@tanstack/react-query';
import { CreditCard, TrendingUp, Building2, AlertTriangle } from 'lucide-react';
import { StripeConfigPanel } from './StripeConfigPanel';
import { NfseConfigPanel } from './NfseConfigPanel';
import { api } from '../../api/client';

function useBillingMetricas() {
  return useQuery({
    queryKey: ['billing-metricas'],
    queryFn: async () => {
      const { data } = await api.get('/billing/metricas');
      return data;
    },
    refetchInterval: 60000,
  });
}

function usePlanos() {
  return useQuery({
    queryKey: ['billing-planos'],
    queryFn: async () => {
      const { data } = await api.get('/billing/planos');
      return data;
    },
  });
}

const STATUS_BADGE: Record<string, { bg: string; color: string; label: string }> = {
  // API keys (from obterMetricas)
  ativas: { bg: '#dcfce7', color: '#166534', label: 'Ativas' },
  trial: { bg: '#dbeafe', color: '#1e40af', label: 'Trial' },
  inadimplentes: { bg: '#fee2e2', color: '#991b1b', label: 'Inadimplentes' },
  suspensas: { bg: '#fef3c7', color: '#92400e', label: 'Suspensas' },
  canceladas: { bg: '#f3f4f6', color: '#6b7280', label: 'Canceladas' },
  // Enum fallbacks
  ATIVA: { bg: '#dcfce7', color: '#166534', label: 'Ativas' },
  TRIAL: { bg: '#dbeafe', color: '#1e40af', label: 'Trial' },
  INADIMPLENTE: { bg: '#fee2e2', color: '#991b1b', label: 'Inadimplentes' },
  SUSPENSA: { bg: '#fef3c7', color: '#92400e', label: 'Suspensas' },
  CANCELADA: { bg: '#f3f4f6', color: '#6b7280', label: 'Canceladas' },
};

export function BillingPanel({ hideKpis = false }: { hideKpis?: boolean } = {}) {
  const { data: metricas, isLoading } = useBillingMetricas();
  const { data: planos } = usePlanos();

  if (isLoading) {
    return (
      <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20, textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted, #9ca3af)' }}>Carregando billing...</p>
      </div>
    );
  }

  if (!metricas) return null;

  const { totalPlanos, totalAssinaturas, porStatus, totalEscolasCobertas, mrrEstimado } = metricas;
  const mrrFormatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(mrrEstimado || 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: hideKpis ? 0 : 24 }}>
      {!hideKpis && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <CreditCard size={18} style={{ color: '#7c3aed' }} /> Billing & Receita
        </h3>
      </div>
      )}

      {!hideKpis && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        {/* MRR */}
        <div style={{
          background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', borderRadius: 14,
          padding: '18px 20px', color: 'white', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -15, right: -15, width: 70, height: 70, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>
            MRR Estimado
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
            {mrrFormatted}
          </div>
          <div style={{ fontSize: '0.62rem', opacity: 0.7, marginTop: 2 }}>
            <TrendingUp size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Receita recorrente mensal
          </div>
        </div>

        {/* Assinaturas */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 14,
          padding: '18px 20px', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', marginBottom: 4 }}>
            Total Assinaturas
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-main, #1e293b)' }}>
            {totalAssinaturas || 0}
          </div>
          <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 2 }}>
            {porStatus?.ativas || 0} ativas · {porStatus?.trial || 0} trial
          </div>
        </div>

        {/* Escolas Cobertas */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 14,
          padding: '18px 20px', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', marginBottom: 4 }}>
            Escolas Cobertas
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-main, #1e293b)' }}>
            {totalEscolasCobertas || 0}
          </div>
          <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 2 }}>
            <Building2 size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Com assinatura ativa
          </div>
        </div>

        {/* Planos */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 14,
          padding: '18px 20px', border: '1px solid var(--border-color, #e5e7eb)',
        }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', marginBottom: 4 }}>
            Planos Ativos
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-main, #1e293b)' }}>
            {totalPlanos || 0}
          </div>
          <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 2 }}>
            Cadastrados no sistema
          </div>
        </div>
      </div>
      )}

      {/* Grid: Status Assinaturas + Planos Comerciais */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Status das Assinaturas */}
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h4 style={{ fontSize: '0.88rem', fontWeight: 700, margin: '0 0 14px', color: 'var(--text-main, #1e293b)' }}>
            Distribuição por Status
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(porStatus || {}).map(([key, val]) => {
              const cfg = STATUS_BADGE[key] || STATUS_BADGE[key.toUpperCase()] || STATUS_BADGE.ativas;
              const count = val as number;
              const pct = totalAssinaturas > 0 ? Math.round((count / totalAssinaturas) * 100) : 0;
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                    background: cfg.bg, color: cfg.color, minWidth: 90, textAlign: 'center',
                  }}>
                    {cfg.label}
                  </span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#f3f4f6', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 4, transition: 'width 0.5s',
                      width: `${pct}%`, background: cfg.color,
                    }} />
                  </div>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', minWidth: 30, textAlign: 'right' }}>
                    {count}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Warning se tem inadimplentes */}
          {(porStatus?.inadimplentes || 0) > 0 && (
            <div style={{
              background: '#fef2f2', borderRadius: 8, padding: '10px 14px', marginTop: 14,
              display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #fecaca',
            }}>
              <AlertTriangle size={14} color="#dc2626" />
              <span style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: 600 }}>
                {porStatus.inadimplentes} assinatura{porStatus.inadimplentes !== 1 ? 's' : ''} inadimplente{porStatus.inadimplentes !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Planos Comerciais */}
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
          <h4 style={{ fontSize: '0.88rem', fontWeight: 700, margin: '0 0 14px', color: 'var(--text-main, #1e293b)' }}>
            Planos Comerciais
          </h4>
          {!planos || planos.length === 0 ? (
            <p style={{ color: 'var(--text-muted, #9ca3af)', textAlign: 'center', padding: 20, fontSize: '0.85rem' }}>
              Nenhum plano cadastrado. Execute o seed.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(planos || []).map((p: any) => {
                const preco = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(p.precoBaseEscola || 0);
                return (
                  <div key={p.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', background: 'var(--bg-main, #f9fafb)', borderRadius: 10,
                    border: '1px solid var(--border-color, #f3f4f6)',
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main, #1e293b)' }}>
                        {p.nome}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>
                        {p.limiteEscolas ? `Até ${p.limiteEscolas} escolas` : 'Ilimitado'} · {p.limiteAlunos ? `${p.limiteAlunos.toLocaleString('pt-BR')} alunos` : 'Ilimitado'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: '#7c3aed' }}>
                        {preco}
                      </div>
                      <div style={{ fontSize: '0.62rem', color: '#9ca3af' }}>
                        /escola/mês
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Stripe Config */}
      <StripeConfigPanel />

      {/* NFS-e Config */}
      <NfseConfigPanel />

      <style>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns: repeat(4"] { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}
